// Radha — legal/recovery escalation reads + writes (migration 053).
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { supabase } from "../lib/supabase";

// Open string, not a union: the available routes are jurisdiction-pack data
// (069), so a fixed TS union would go stale the moment a pack is added.
export type LegalCaseType = string;
export type LegalCaseStatus = "open" | "resolved" | "withdrawn";

export interface LegalCase {
  id: string; account_id: string; case_type: LegalCaseType; status: LegalCaseStatus;
  amount_involved: string;
  notice_reference: string | null; advocate_name: string | null;
  cheque_number: string | null; cheque_bank: string | null; bounce_date: string | null;
  court_name: string | null; next_hearing_date: string | null;
  agency_name: string | null; commission_pct: string | null; amount_recovered: string | null;
  nclt_bench: string | null; nclt_case_number: string | null;
  udyam_number: string | null; msefc_council: string | null;
  bank_rate_annual: string | null; statutory_rate_annual: string | null;
  interest_as_of: string | null; statutory_interest_accrued: string | null; msefc_reference: string | null;
  notes: string | null; opened_at: string; resolved_at: string | null;
}

export function useLegalCases() {
  return useQuery({
    queryKey: ["legal-cases"],
    queryFn: async (): Promise<LegalCase[]> => {
      const { data, error } = await supabase
        .from("legal_cases")
        .select("id, account_id, case_type, status, amount_involved, notice_reference, advocate_name, cheque_number, cheque_bank, bounce_date, court_name, next_hearing_date, agency_name, commission_pct, amount_recovered, nclt_bench, nclt_case_number, udyam_number, msefc_council, bank_rate_annual, statutory_rate_annual, interest_as_of, statutory_interest_accrued, msefc_reference, notes, opened_at, resolved_at")
        .order("opened_at", { ascending: false });
      if (error) throw error;
      return data ?? [];
    },
    staleTime: 15_000,
  });
}

export function useOpenLegalCase() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { account_id: string; case_type: LegalCaseType; amount_involved: number } & Record<string, unknown>) => {
      const { error } = await supabase.from("legal_cases").insert(input);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["legal-cases"] }),
  });
}

export function useUpdateLegalCase() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...patch }: { id: string } & Record<string, unknown>) => {
      const { error } = await supabase.from("legal_cases").update(patch).eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["legal-cases"] }),
  });
}

export interface StatutoryInterest {
  is_statutory: boolean;
  regime_name: string;
  statute_ref: string | null;
  reference_rate: string | null;
  reference_as_of: string | null;
  reference_source: string | null;
  applied_rate: string | null;
  compounding: string;
  days: number;
  interest: string;
  fixed_compensation: string;
  compensation_currency: string | null;
  total: string;
  notes: string | null;
}

export interface LegalCaseTypeOption {
  case_type: string; label: string; statute_ref: string | null; sort_order: number;
}

/** Escalation routes available in this jurisdiction (069). The DB rejects any
 *  route not in the tenant's pack, so this keeps the UI honest rather than
 *  offering a §138 cheque-bounce case to an American team. */
export function useLegalCaseTypes(country: string | null) {
  return useQuery({
    queryKey: ["legal-case-types", country],
    queryFn: async (): Promise<LegalCaseTypeOption[]> => {
      const { data, error } = await supabase
        .from("legal_case_types")
        .select("case_type, label, statute_ref, sort_order")
        .eq("country", country as string)
        .order("sort_order", { ascending: true });
      if (error) throw error;
      return data ?? [];
    },
    enabled: !!country,
    staleTime: 300_000,
  });
}

/**
 * Statutory late-payment interest for one debt, computed in the database
 * (068) rather than in the component. The old client-side estimate multiplied
 * a hand-typed bank rate by three — correct only for India, and unauditable
 * because nothing recorded which rate was used. This returns the full working
 * so the figure can be checked before it goes on a filing.
 */
export function useStatutoryInterest(country: string | null, principal: number, fromDate: string | null) {
  return useQuery({
    queryKey: ["statutory-interest", country, principal, fromDate],
    queryFn: async (): Promise<StatutoryInterest | null> => {
      const { data, error } = await supabase.rpc("compute_statutory_interest", {
        p_country: country, p_principal: principal, p_from: fromDate,
      });
      if (error) throw error;
      return (data as StatutoryInterest[])?.[0] ?? null;
    },
    enabled: !!country && !!fromDate && principal > 0,
    staleTime: 60_000,
  });
}

export function legalErrorMessage(err: unknown): string {
  const msg = err instanceof Error ? err.message : String(err);
  const m = msg.match(/^([A-Z_]+):\s*(.*)$/);
  return m ? m[2] || m[1] : msg;
}
